import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  DataObject as StateIcon,
  Public as GlobalIcon,
  Timeline as FlowIcon,
  DeviceHub as NodeIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import uniq from 'lodash/uniq';
import { useFlowContext } from '../../contexts/FlowContext.jsx';

function StateManagementTab({
  onCreateVar, onEditVar, onDeleteVar, selectedNode, selectedScope, setSelectedScope, nodes = [],
}) {
  const [activeTab, setActiveTab] = useState(() => {
    if (selectedScope === 'flow') return 1;
    if (selectedScope === 'node') return 2;
    return 0;
  });
  const [showHistory, setShowHistory] = useState(false);

  let context;
  try {
    context = useFlowContext();
  } catch (e) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="error" variant="body2">
          StateManagementTab must be used inside a FlowContextProvider.
          <br />
          (MCP context is not available.)
        </Typography>
      </Box>
    );
  }

  const {
    getAllVarsWithMetadata,
    currentNodeId,
    deleteVar,
    rollbackVar,
    lastUpdate,
  } = context;

  // Get all nodes in the flow from props
  // const { nodes } = context; // Removed - nodes now come from props

  // 1. Collect all flow variable names from all nodes
  const allFlowVarNames = uniq(
    (nodes || []).flatMap((node) => (node.data?.stateVars || [])),
  );

  // 2. Get actual flow context values
  const flowVars = context.flowVars || {};

  // 3. Build a merged object for display in Flow tab
  const mergedFlowVars = {};
  allFlowVarNames.forEach((varName) => {
    mergedFlowVars[varName] = flowVars[varName] !== undefined
      ? { value: flowVars[varName], type: typeof flowVars[varName] }
      : { value: undefined, type: 'unset' };
  });

  // 4. For Node tab, only show node-local variables (nodeStateVars)
  const allNodeVarNames = uniq(
    (selectedNode?.data?.nodeStateVars || []),
  );
  const nodeVars = {};
  allNodeVarNames.forEach((varName) => {
    nodeVars[varName] = { value: undefined, type: 'unset' };
  });

  // 5. Use mergedFlowVars for Flow tab, nodeVars for Node tab
  const getVarsForScope = () => {
    if (selectedScope === 'global') {
      return getAllVarsWithMetadata('global');
    } if (selectedScope === 'flow') {
      return mergedFlowVars; // Use merged vars for flow scope
    } if (selectedScope === 'node') {
      return nodeVars; // Only show node-local vars at design time
    }
    return {};
  };

  // Sync activeTab <-> selectedScope
  useEffect(() => {
    setActiveTab(selectedScope === 'flow' ? 1 : selectedScope === 'node' ? 2 : 0);
  }, [selectedScope]);

  useEffect(() => {
    if (setSelectedScope) setSelectedScope(['global', 'flow', 'node'][activeTab]);
  }, [activeTab, setSelectedScope]);

  const handleCreateVar = () => {
    if (onCreateVar) onCreateVar(selectedScope, currentNodeId);
  };

  const handleEditVar = (key, varData) => {
    if (onEditVar) onEditVar(key, varData, selectedScope, currentNodeId);
  };

  const handleDeleteVar = (key) => {
    deleteVar(key, selectedScope, selectedScope === 'node' ? currentNodeId : null);
    if (onDeleteVar) onDeleteVar(key, selectedScope, selectedScope === 'node' ? currentNodeId : null);
  };

  const handleRollbackVar = (key, version) => {
    rollbackVar(key, selectedScope, selectedScope === 'node' ? currentNodeId : null, version);
  };

  const vars = getVarsForScope();

  // Use lastUpdate to force re-render when context changes
  useEffect(() => {}, [lastUpdate, currentNodeId]);

  // Helper function to get type color
  const getTypeColor = (type) => {
    const typeColors = {
      string: 'primary',
      number: 'success',
      boolean: 'warning',
      object: 'secondary',
      array: 'error',
      unset: 'default',
    };
    return typeColors[type] || 'default';
  };

  // Helper function to get type display
  const getTypeDisplay = (varData) => {
    // First check if type is explicitly set in metadata
    if (varData.metadata?.type) {
      return varData.metadata.type;
    }

    // Check if type is directly set
    if (varData.type && varData.type !== 'unset') {
      return varData.type;
    }

    // Infer type from value if not explicitly set
    if (varData.value === null || varData.value === undefined) {
      return 'unset';
    }
    if (Array.isArray(varData.value)) {
      return 'array';
    }
    if (typeof varData.value === 'object' && varData.value !== null) {
      return 'object';
    }
    return typeof varData.value;
  };

  // Helper function to get value display
  const getValueDisplay = (varData) => {
    if (varData.value === null || varData.value === undefined) {
      return 'undefined';
    }
    if (typeof varData.value === 'object') {
      return JSON.stringify(varData.value);
    }
    return String(varData.value);
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2,
      }}
      >
        <Typography variant="h6">State Management</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateVar}
        >
          Add State Variable
        </Button>
      </Box>

      {/* Scope selector */}
      <Tabs
        value={activeTab}
        onChange={(_, newValue) => {
          setActiveTab(newValue);
        }}
        sx={{ mb: 2 }}
      >
        <Tab icon={<GlobalIcon />} label="Global" />
        <Tab icon={<FlowIcon />} label="Flow" />
        <Tab icon={<NodeIcon />} label="Node" disabled={!currentNodeId} />
      </Tabs>

      <Paper sx={{ flex: 1, p: 2, overflow: 'auto' }}>
        {Object.entries(vars).length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <StateIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No state variables defined in
              {' '}
              {selectedScope}
              {' '}
              scope
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Define
              {' '}
              {selectedScope}
              {' '}
              state variables to share data between nodes
            </Typography>
            <Button variant="contained" onClick={handleCreateVar}>
              Create First Variable
            </Button>
          </Box>
        ) : (
          <List>
            {Object.entries(vars).map(([key, varData]) => {
              const typeDisplay = getTypeDisplay(varData);
              const valueDisplay = getValueDisplay(varData);

              return (
                <ListItem key={key} divider>
                  <ListItemText
                    primary={(
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {key}
                        </Typography>
                        <Chip
                          label={typeDisplay}
                          size="small"
                          color={getTypeColor(typeDisplay)}
                          variant={typeDisplay === 'unset' ? 'outlined' : 'filled'}
                        />
                        {varData.persistent && (
                          <Chip label="Persistent" size="small" color="secondary" />
                        )}
                      </Box>
                    )}
                    secondary={(
                      <>
                        <span style={{ display: 'block', marginBottom: '4px' }}>
                          {varData.description || 'No description'}
                        </span>
                        <span style={{ display: 'block', fontSize: '0.75rem', color: 'rgba(0, 0, 0, 0.6)' }}>
                          Value:
                          {' '}
                          {valueDisplay}
                        </span>
                        {varData.updatedAt && (
                          <span style={{ display: 'block', fontSize: '0.75rem', color: 'rgba(0, 0, 0, 0.6)' }}>
                            Last Updated:
                            {' '}
                            {new Date(varData.updatedAt).toLocaleString()}
                            {varData.updatedBy !== 'system' ? ` by ${varData.updatedBy}` : ''}
                          </span>
                        )}
                      </>
                    )}
                  />
                  <ListItemSecondaryAction>
                    <IconButton onClick={() => setShowHistory((prev) => !prev)} title="Show history">
                      <HistoryIcon />
                    </IconButton>
                    <IconButton onClick={() => handleEditVar(key, varData)} title="Edit">
                      <EditIcon />
                    </IconButton>
                    <IconButton onClick={() => handleDeleteVar(key)} color="error" title="Delete">
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              );
            })}
          </List>
        )}
      </Paper>
    </Box>
  );
}

export default StateManagementTab;
